import React, { useState } from 'react';
import styles from './authUi.module.css';

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 4l16 16" strokeLinecap="round" />
      <path d="M9.9 5.8A9.9 9.9 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-2.8 3.7M6.4 7.7A17.4 17.4 0 0 0 2.5 12S6 18.5 12 18.5c1 0 1.9-.2 2.8-.5" />
      <path d="M9.9 10a3.2 3.2 0 0 0 4.3 4.4" />
    </svg>
  );
}

/**
 * Labelled input for the auth pages. Password fields get a reveal toggle: these
 * are the screens where a mistyped password is most costly, and on mobile it is
 * the difference between one attempt and three.
 *
 * Placeholders are kept alongside the visible label because they are load-bearing
 * for existing tests and for password managers.
 */
export default function AuthField({
  id,
  label,
  type = 'text',
  hint,
  requirementHint,
  labelAction,
  inputRef,
  autoFocus,
  ...inputProps
}) {
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  const hintId = hint ? `${id}-hint` : undefined;

  // On a phone, autofocus throws up the keyboard before the reader has seen the
  // form, covering half of it. Desktop keeps the focus so the page is typeable
  // straight away.
  const coarsePointer =
    typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        {labelAction}
      </div>

      <div className={styles.inputWrap}>
        <input
          {...inputProps}
          id={id}
          ref={inputRef}
          autoFocus={autoFocus && !coarsePointer}
          type={isPassword && revealed ? 'text' : type}
          className={`${styles.input} ${isPassword ? styles.inputWithAction : ''}`}
          aria-describedby={hintId}
        />
        {isPassword && (
          <button
            type="button"
            className={styles.inputAction}
            onClick={() => setRevealed((shown) => !shown)}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
          >
            {revealed ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        )}
      </div>

      {hint && (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      )}

      {/* Held back until what has been typed actually fails the pattern — see
          .requirementHint. The browser's own validation message on submit is the
          backstop where :has() is unsupported. */}
      {requirementHint && (
        <p className={`${styles.hint} ${styles.requirementHint}`}>{requirementHint}</p>
      )}
    </div>
  );
}
